import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IApplicationState } from '../../Store';
import { fetchFormulaire } from '../../Store/Formulaire/Actions';
import { 
  Grid, 
  Typography, 
  Box, 
  Paper, 
  Divider, 
  Card, 
  CardContent, 
  Chip,
  Link,
  CircularProgress,
  Container
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import InfoIcon from '@material-ui/icons/Info';
import LaunchRoundedIcon from '@material-ui/icons/LaunchRounded';
import PersonIcon from '@material-ui/icons/Person';
import MovieIcon from '@material-ui/icons/Movie';
import MusicNoteIcon from '@material-ui/icons/MusicNote';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { IMusicInfo } from '../../Store/Formulaire/Types';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '90%',
    margin: '0 auto',
    padding: theme.spacing(3),
  },
  sectionCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  fieldRow: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  fieldLabel: {
    fontWeight: 'bold',
    minWidth: '200px',
    color: theme.palette.text.secondary,
  },
  fieldValue: {
    flex: 1,
    color: theme.palette.text.primary,
  },
  adminHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  workItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface AdminPreviewProps {
  fgpId: number;
  bucketId: number;
  userGroupId: number;
}

export const AdminPreview: React.FC<AdminPreviewProps> = ({
  fgpId,
  bucketId,
  userGroupId
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  
  // Récupérer les données du store
  const formulaire = useSelector((state: IApplicationState) => state.formulaire.fr);
  const isLoading = useSelector((state: IApplicationState) => state.formulaire.isLoading);

  useEffect(() => {
    // Créer la fingerprint list à partir des paramètres
    const fingerprintList = [{
      bucket_id: bucketId,
      fgp_id: fgpId
    }];

    // Dispatcher l'action pour récupérer les données
    dispatch(fetchFormulaire({
      fingerprint_list: fingerprintList,
      user_group_id: userGroupId
    }));
  }, [dispatch, bucketId, fgpId, userGroupId]);

  if (isLoading) {
    return (
      <Container className={classes.root}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="h6" style={{ marginLeft: 16 }}>
            Chargement des données du formulaire...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!formulaire || formulaire.spot_id === -1) {
    return (
      <Container className={classes.root}>
        <Typography variant="h6" color="error">
          Aucune donnée trouvée pour ce formulaire
        </Typography>
      </Container>
    );
  }

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'completed': return 'primary';
      case 'signed': return 'secondary';
      case 'sent': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateValue?: string | number | null) => {
    if (!dateValue) return 'Non renseigné';
    try {
      return new Date(dateValue).toLocaleDateString('fr-FR');
    } catch (error) {
      return 'Date invalide';
    }
  };

  return (
    <Container className={classes.root}>
      {/* En-tête Admin */}
      <Paper className={classes.adminHeader}>
        <Typography variant="h4" component="h1">
          Aperçu Administrateur - Déclaration
        </Typography>
        <Typography variant="subtitle1">
          Consultation du formulaire pour le groupe utilisateur #{userGroupId}
        </Typography>
        {formulaire.envelope_status && (
          <Chip 
            label={`Statut: ${formulaire.envelope_status}`}
            color={getStatusColor(formulaire.envelope_status)}
            className={classes.statusChip}
          />
        )}
      </Paper>

      {/* Section Spot */}
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h5" className={classes.sectionTitle}>
            <MovieIcon color="primary" />
            Informations du Spot
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Code IDCrea:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.id_crea_code || 'Non renseigné'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Marque:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.marque || 'Non renseigné'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Nom du produit:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.product_name || 'Non renseigné'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Nature du produit:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.product_nature || 'Non renseigné'}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Annonceur:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.announcer || 'Non renseigné'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Titre du spot:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.spot_title || 'Non renseigné'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Durée (sec):</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.duration || 'Non renseigné'}
                </Typography>
              </div>
            </Grid>
          </Grid>

          <Box mt={2}>
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>Code ARPP:</Typography>
              <Typography className={classes.fieldValue}>
                {formulaire.codes_arpp || 'Non renseigné'}
              </Typography>
            </div>
            <div className={classes.fieldRow}>
              <Typography className={classes.fieldLabel}>Code PubID:</Typography>
              <Typography className={classes.fieldValue}>
                {formulaire.codes_pub_id || 'Non renseigné'}
              </Typography>
            </div>
          </Box>
        </CardContent>
      </Card>

      {/* Section Première Diffusion */}
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h5" className={classes.sectionTitle}>
            <CalendarTodayIcon color="primary" />
            Première Diffusion
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Média:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.media.name || 'Non renseigné'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Date de diffusion:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.diffusion_date ? formatDate(formulaire.diffusion_date) : 'Non renseigné'}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Chaîne/Station:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.diffuser || 'Non renseigné'}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Section Œuvres */}
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h5" className={classes.sectionTitle}>
            <MusicNoteIcon color="primary" />
            Œuvres Musicales ({formulaire.music?.length || 0})
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          
          {formulaire.music && formulaire.music.length > 0 ? (
            (formulaire.music as IMusicInfo[]).map((music: any, index: number) => (
              <Box key={index} className={classes.workItem}>
                <Typography variant="h6" color="primary" gutterBottom>
                  Œuvre #{index + 1}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <div className={classes.fieldRow}>
                      <Typography className={classes.fieldLabel}>Titre:</Typography>
                      <Typography className={classes.fieldValue}>
                        {music.title || 'Non renseigné'}
                      </Typography>
                    </div>
                    <div className={classes.fieldRow}>
                      <Typography className={classes.fieldLabel}>Durée utilisée:</Typography>
                      <Typography className={classes.fieldValue}>
                        {music.duration_used || 'Non renseigné'}
                      </Typography>
                    </div>
                    <div className={classes.fieldRow}>
                      <Typography className={classes.fieldLabel}>Code ISWC:</Typography>
                      <Typography className={classes.fieldValue}>
                        {music.iswc_code || 'Non renseigné'}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {music.rightholders && music.rightholders.length > 0 && (
                      <div>
                        <Typography variant="subtitle2" color="primary">
                          Ayants droit:
                        </Typography>
                        {music.rightholders.map((rh: any, rhIndex: number) => (
                          <div key={rhIndex} style={{ marginLeft: 16, marginBottom: 8 }}>
                            <Typography variant="body2">
                              <strong>{rh.role}:</strong> {rh.name || 'Non renseigné'}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    )}
                  </Grid>
                </Grid>
              </Box>
            ))
          ) : (
            <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
              Aucune œuvre musicale déclarée
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Section Déclarant */}
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h5" className={classes.sectionTitle}>
            <PersonIcon color="primary" />
            Informations du Déclarant
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Nom:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.society?.name || 'Non renseigné'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Email:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.society?.email || 'Non renseigné'}
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Validé:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.is_validated ? 'Oui' : 'Non'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Signé:</Typography>
                <Typography className={classes.fieldValue}>
                  {formulaire.is_signed ? 'Oui' : 'Non'}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Date de création:</Typography>
                <Typography className={classes.fieldValue}>
                  {formatDate(formulaire.created_at?.$date)}
                </Typography>
              </div>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Dernière modification:</Typography>
                <Typography className={classes.fieldValue}>
                  {formatDate(formulaire.last_modified?.$date)}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Informations techniques */}
      <Card className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h5" className={classes.sectionTitle}>
            <InfoIcon color="primary" />
            Informations Techniques
          </Typography>
          <Divider style={{ marginBottom: 16 }} />
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Bucket ID:</Typography>
                <Typography className={classes.fieldValue}>{bucketId}</Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={3}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>FGP ID:</Typography>
                <Typography className={classes.fieldValue}>{fgpId}</Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={3}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>User Group ID:</Typography>
                <Typography className={classes.fieldValue}>{userGroupId}</Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={3}>
              <div className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Spot ID:</Typography>
                <Typography className={classes.fieldValue}>{formulaire.spot_id}</Typography>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};
